import { useState } from 'react'
import { Eye, TrendingUp, TrendingDown, Minus, AlertTriangle, Building2, MapPin, Calendar, Phone, Mail } from 'lucide-react'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Business, TLBHScore } from '@/types'

interface BusinessSearchResult {
  business: Business
  score: TLBHScore
  riskLevel: 'low' | 'medium' | 'high' | 'critical'
  lastUpdated: string
  alerts: number
  trend: 'improving' | 'stable' | 'declining'
}

interface BusinessSearchResultsProps {
  results: BusinessSearchResult[]
  isLoading: boolean
  onViewBusiness: (business: BusinessSearchResult) => void
}

export function BusinessSearchResults({ results, isLoading, onViewBusiness }: BusinessSearchResultsProps) {
  const [sortBy, setSortBy] = useState<'score' | 'name' | 'risk' | 'updated'>('score')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list')

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'low': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900'
      case 'medium': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900'
      case 'high': return 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900'
      case 'critical': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900'
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900'
    }
  }

  const getBandColor = (band: string) => {
    switch (band) {
      case 'A+':
      case 'A': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900'
      case 'B+':
      case 'B': return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900'
      case 'C+':
      case 'C': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900'
      case 'D': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900'
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900'
    }
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="w-4 h-4 text-green-600" />
      case 'declining': return <TrendingDown className="w-4 h-4 text-red-600" />
      case 'stable': return <Minus className="w-4 h-4 text-gray-600" />
      default: return null
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const sortedResults = [...results].sort((a, b) => {
    let aValue: any, bValue: any

    switch (sortBy) {
      case 'score':
        aValue = a.score.tlbhScore
        bValue = b.score.tlbhScore
        break
      case 'name':
        aValue = a.business.name.toLowerCase()
        bValue = b.business.name.toLowerCase()
        break
      case 'risk':
        const riskOrder = { low: 1, medium: 2, high: 3, critical: 4 }
        aValue = riskOrder[a.riskLevel]
        bValue = riskOrder[b.riskLevel]
        break
      case 'updated':
        aValue = new Date(a.lastUpdated).getTime()
        bValue = new Date(b.lastUpdated).getTime()
        break
      default:
        return 0
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1
    } else {
      return aValue < bValue ? 1 : -1
    }
  })

  const handleSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('desc')
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8">
        <div className="flex items-center justify-center">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Searching businesses...</span>
        </div>
      </div>
    )
  }

  if (results.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8">
        <div className="text-center">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No businesses found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Try adjusting your search criteria or filters to find more results.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Results Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Sort by:
            </span>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-')
                setSortBy(field as typeof sortBy)
                setSortOrder(order as typeof sortOrder)
              }}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="score-desc">Score (High to Low)</option>
              <option value="score-asc">Score (Low to High)</option>
              <option value="name-asc">Name (A to Z)</option>
              <option value="name-desc">Name (Z to A)</option>
              <option value="risk-asc">Risk (Low to High)</option>
              <option value="risk-desc">Risk (High to Low)</option>
              <option value="updated-desc">Recently Updated</option>
              <option value="updated-asc">Oldest Updated</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-primary-100 text-primary-600 dark:bg-primary-900 dark:text-primary-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600 dark:bg-primary-900 dark:text-primary-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Results List */}
      <div className={viewMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 gap-6 p-6' : 'divide-y divide-gray-200 dark:divide-gray-700'}>
        {sortedResults.map((result) => (
          <div
            key={result.business.id}
            className={`${viewMode === 'list' ? 'p-6 hover:bg-gray-50 dark:hover:bg-gray-700' : 'border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md'} transition-colors cursor-pointer`}
            onClick={() => onViewBusiness(result)}
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-4 sm:space-y-0">
              {/* Business Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                        {result.business.name}
                      </h3>
                      {result.alerts > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {result.alerts}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span className="inline-flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        {result.business.address.city}, {result.business.address.state}
                      </span>
                      <span>•</span>
                      <span>{result.business.industry}</span>
                      <span>•</span>
                      <span className="inline-flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        Est. {new Date(result.business.registrationDate).getFullYear()}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span>PAN: {result.business.pan}</span>
                      <span>•</span>
                      <span>GSTIN: {result.business.gstin}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Score & Risk Info */}
              <div className="flex-shrink-0 text-right">
                <div className="flex flex-col items-end space-y-2">
                  {/* TLBH Score */}
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {result.score.tlbhScore.toFixed(2)}
                    </div>
                    <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getBandColor(result.score.band)}`}>
                      {result.score.band}
                    </div>
                  </div>

                  {/* Risk Level */}
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRiskLevelColor(result.riskLevel)}`}>
                    {result.riskLevel.charAt(0).toUpperCase() + result.riskLevel.slice(1)} Risk
                  </div>

                  {/* Trend */}
                  <div className="flex items-center space-x-1">
                    {getTrendIcon(result.trend)}
                    <span className="text-xs text-gray-600 dark:text-gray-400 capitalize">
                      {result.trend}
                    </span>
                  </div>

                  {/* Last Updated */}
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Updated {formatDate(result.lastUpdated)}
                  </div>

                  {/* View Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onViewBusiness(result)
                    }}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    View Details
                  </button>
                </div>
              </div>
            </div>

            {/* Dimension Scores (List View Only) */}
            {viewMode === 'list' && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {Object.entries(result.score.dimensionScores).map(([key, dimension]) => (
                    <div key={key} className="text-center">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {parseFloat(dimension.score.toString()).toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                        {key}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}